import React from 'react';
import {
  KatAlert,
  KatButton,
  KatDataTable,
  KatPagination,
  KatSpinner,
} from '@amzn/katal-react';
import { ApolloError } from 'apollo-client';
import {
  DashboardBreakdownResult,
  MetricsSortField,
  SortDirection,
} from 'src/generated/graphql';
import { useTranslation } from 'src/i18next-arb-shim/useTranslation';
import dashboardBreakdownColumnConfig from './DashboardBreakdownColumnConfig';
import style from './DashboardBreakdownTable.module.scss';

export type SortState = {
  field: MetricsSortField;
  direction: SortDirection;
};

type Props = {
  loading: boolean;
  error: ApolloError | undefined;
  data: DashboardBreakdownResult | undefined;
  sort: SortState;
  page: number; // 1-based, matches KatPagination convention
  pageSize: number;
  onSortChange: (sort: SortState) => void;
  onPageChange: (page: number) => void;
  onRetry: () => void;
};

// Maps a column property name from the table's sort event to the wire enum
// the backend expects. Property names in the column config are kept identical
// to MetricsSortField string values so this mapping is a 1:1 cast.
const columnPropertyToSortField = (
  property: string
): MetricsSortField | undefined => {
  const valid = Object.values(MetricsSortField) as string[];
  return valid.includes(property) ? (property as MetricsSortField) : undefined;
};

// KatDataTable emits sortDirection as 'asc' | 'desc'. Backend expects A | D.
const directionFromKatToWire = (direction: string): SortDirection =>
  direction.toLowerCase() === 'asc' ? SortDirection.A : SortDirection.D;

const DashboardBreakdownTable: React.FC<Props> = (props: Props) => {
  const { t } = useTranslation();

  if (props.loading && !props.data) {
    return (
      <div className={style.spinnerWrapper}>
        <KatSpinner size="large" />
      </div>
    );
  }

  if (props.error) {
    return (
      <KatAlert
        variant="danger"
        header={t('lui-dashboard-breakdown-error-header')}
        description={props.error.message}
        persistent={true}
        cta={
          <KatButton variant="primary" onClick={props.onRetry}>
            {t('lui-dashboard-breakdown-retry')}
          </KatButton>
        }
      />
    );
  }

  if (!props.data) {
    return null;
  }

  const { metrics, totalCount, totalDistinctPackages } = props.data;

  // Empty state — backend returns metrics: [] (never null) when no rows match.
  if (metrics.length === 0) {
    return (
      <div className={style.empty}>{t('lui-dashboard-breakdown-empty')}</div>
    );
  }

  // totalCount is clamped at the backend's 10K bucket cap. When the unclamped
  // distinct-packages count exceeds it, we surface a "showing first N of M"
  // notice so users know to refine filters for full coverage.
  const showingClampedNotice = totalDistinctPackages > totalCount;

  // KatPagination is 1-based; computes its own page count from totalItems
  // and itemsPerPage, so we just pass those plus the current page through.
  const currentPage = props.page;

  const onSortingChange = (e: KatDataTable.SettingsChangedEvent) => {
    if (!e.detail.sortColumn) return;
    const field = columnPropertyToSortField(e.detail.sortColumn.property);
    if (!field) return;
    const direction = directionFromKatToWire(e.detail.sortDirection);
    if (props.sort.field === field && props.sort.direction === direction) {
      return; // no change
    }
    props.onSortChange({ field, direction });
  };

  const onPaginationChange = (e: KatPagination.ChangeEvent) => {
    const selectedPage = e.detail.page;
    if (selectedPage === currentPage) return;
    props.onPageChange(selectedPage);
  };

  return (
    <div id="dashboard-breakdown-table">
      {showingClampedNotice && (
        <div className={style.clampedNotice}>
          {t('lui-dashboard-breakdown-clamped-notice', {
            shown: totalCount.toLocaleString(),
            total: totalDistinctPackages.toLocaleString(),
          })}
        </div>
      )}
      <KatDataTable
        id="dashboard-breakdown-data-table"
        columns={dashboardBreakdownColumnConfig(t)}
        rowData={metrics}
        onSettingsChanged={onSortingChange}
      />
      <KatPagination
        id="dashboard-breakdown-pagination"
        itemsPerPage={props.pageSize}
        onChange={onPaginationChange}
        page={currentPage}
        totalItems={totalCount}
      />
    </div>
  );
};

export default DashboardBreakdownTable;
